import { useState } from 'react';
import { Modal } from 'src/components/baseComponents';
import { useAuthStore } from 'src/hooks/auth';
import { analytics } from 'src/services/analytics';
import { getAppointmentDetailsForAnalytics } from 'src/utils';
import { useFormStore } from 'src/hooks/form';
import { useScreenSize } from '@insight-health-ai/roseate/utils';
import { useChatValidationStore } from 'src/hooks/chat';

export const Medications: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const [loading, setLoading] = useState(false);
  const { appointment } = useAuthStore();
  const { reviewDetails, saveMedicationsAndAllergies } = useFormStore();
  const { mediaMax } = useScreenSize();
  const { token } = useChatValidationStore();
  const isMobile = mediaMax('sm');
  const [medicationArray, setMedicationArray] = useState<string[]>(
    reviewDetails?.medications || []
  );
  const [allergiesArray, setAllergiesArray] = useState<string[]>(
    reviewDetails?.allergies || []
  );
  const [newTag, setNewTag] = useState({
    medication: '',
    allergies: '',
  });
  const [isInputVisible, setIsInputVisible] = useState({
    medication: false,
    allergies: false,
  });

  const handleEnterKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement> & {
      target: { name: keyof typeof newTag };
    }
  ) => {
    e.key === 'Enter' && e.preventDefault();
    if (e.key === 'Enter' && newTag?.[e.target.name]) {
      if (e.target.name === 'medication') {
        setMedicationArray([...medicationArray, newTag[e.target.name].trim()]);
        analytics.track(
          'Added Medications',
          getAppointmentDetailsForAnalytics(appointment!)
        );
      } else {
        setAllergiesArray([...allergiesArray, newTag[e.target.name].trim()]);
        analytics.track(
          'Added Allergies',
          getAppointmentDetailsForAnalytics(appointment!)
        );
      }
      setNewTag({ ...newTag, [e.target.name]: '' });
    }
  };

  const handleAddTag = (
    e: React.KeyboardEvent<HTMLInputElement> & {
      target: { name: keyof typeof newTag };
    }
  ) => {
    if (newTag[e.target.name as keyof typeof newTag]) {
      e.target.name === 'medication'
        ? setMedicationArray([...medicationArray, newTag[e.target.name].trim()])
        : setAllergiesArray([...allergiesArray, newTag[e.target.name].trim()]);
    }
    setNewTag({ ...newTag, [e.target.name]: '' });
    setIsInputVisible({ ...isInputVisible, [e.target.name]: false });
  };

  const handleRemoveTag = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    const updatedArray =
      (e.target as any).name === 'medication'
        ? [...medicationArray]
        : [...allergiesArray];
    updatedArray.splice(index, 1);
    (e.target as any).name === 'medication'
      ? setMedicationArray(updatedArray)
      : setAllergiesArray(updatedArray);
  };

  const fieldsForMobile = [
    {
      type: 'tagify',
      activeInputField: 'medications',
      title: 'Medications',
      name: 'medication',
      autoComplete: 'off',
      tags: medicationArray,
      newTag: newTag,
      setNewTag: setNewTag,
      isInputVisible: isInputVisible,
      setIsInputVisible: setIsInputVisible,
      handleEnterKeyPress: handleEnterKeyPress,
      handleAddTag: handleAddTag,
      handleRemoveTag: handleRemoveTag,
      className: 'col-span-2 mb-0',
    },
    {
      type: 'tagify',
      activeInputField: 'allergies',
      title: 'Allergies',
      name: 'allergies',
      autoComplete: 'off',
      tags: allergiesArray,
      newTag: newTag,
      setNewTag: setNewTag,
      isInputVisible: isInputVisible,
      setIsInputVisible: setIsInputVisible,
      handleEnterKeyPress: handleEnterKeyPress,
      handleAddTag: handleAddTag,
      handleRemoveTag: handleRemoveTag,
      className: 'col-span-2 mb-0',
    },
  ];

  const fieldsForDesktop = [
    {
      type: 'tagify',
      activeInputField: 'medications',
      title: 'Medications',
      name: 'medication',
      autoComplete: 'off',
      tags: medicationArray,
      newTag: newTag,
      setNewTag: setNewTag,
      isInputVisible: isInputVisible,
      setIsInputVisible: setIsInputVisible,
      handleEnterKeyPress: handleEnterKeyPress,
      handleAddTag: handleAddTag,
      handleRemoveTag: handleRemoveTag,
    },
    {
      type: 'tagify',
      activeInputField: 'allergies',
      title: 'Allergies',
      name: 'allergies',
      autoComplete: 'off',
      tags: allergiesArray,
      newTag: newTag,
      setNewTag: setNewTag,
      isInputVisible: isInputVisible,
      setIsInputVisible: setIsInputVisible,
      handleEnterKeyPress: handleEnterKeyPress,
      handleAddTag: handleAddTag,
      handleRemoveTag: handleRemoveTag,
    },
  ];

  const fields = isMobile ? fieldsForMobile : fieldsForDesktop;

  const handleSubmit = async () => {
    const updatedMedicationArray = newTag.medication
      ? [...medicationArray, newTag.medication.trim()]
      : medicationArray;
    const updatedAllergiesArray = newTag.allergies
      ? [...allergiesArray, newTag.allergies.trim()]
      : allergiesArray;
    analytics.track(
      'Confirmed and Ended Chat',
      getAppointmentDetailsForAnalytics(appointment!)
    );
    setLoading(true);
    await saveMedicationsAndAllergies({
      chatToken: token!,
      medications: updatedMedicationArray.join(' ,').trim(),
      allergies: updatedAllergiesArray.join(' ,').trim(),
    });
    setLoading(false);
  };

  return (
    <div>
      {/* @ts-ignore */}
      <Modal
        open={isOpen}
        fields={fields}
        closable={false}
        className={'review-modal'}
        onSubmitLoading={loading}
        disableSubmit={loading}
        heading={
          <div
            className="modal__form-el-heading mt-3 mb-3"
            style={{ fontWeight: 600 }}
          >
            Just making sure your medications and allergies are accurate. You
            can add or remove below :
          </div>
        }
        formClassName={
          isMobile ? 'grid grid-cols-1 gap-8' : 'grid grid-cols-2 gap-8'
        }
        submitButtonText="Confirm and End Chat"
        centered={true}
        cancelButtonText=""
        handleSubmit={handleSubmit}
      />
    </div>
  );
};
